// Work — the single tabbed surface for native work items (Universal Work §7/§10):
// My Work · Created by Me · Collaborating · Team Work · Reviews · Workload · Templates.
// Every tab is a server filter; tab visibility follows the tenant policy's `me`
// block + permissions. Hiding is UX only — every call still fails gracefully
// with the backend message (backend-error-surfacing rule).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { ClipboardList, Plus, Settings2 } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/features/shared/components/ui/select';
import { getApiErrorMessage } from '@/lib/apiError';
import { useHasPermission } from '@/rbac/useRbac';
import { SubitemRollup, WorkItem, WorkProject, listProjects, listWorkItems } from '@/services/workService';
import { usePeopleNames, useWorkPolicy } from '@/features/work/api/hooks';
import { CreateWorkDialog, ManageTemplatesDialog, WorkStatusBadge } from './shared';
import { WorkList, OPEN_KEY } from './WorkList';
import { WorkloadPage } from './workload/WorkloadPage';
import { fmtDT } from '../workassignments/shared';

export type WorkTab = 'my' | 'created' | 'collaborating' | 'team' | 'reviews' | 'workload' | 'templates';
const ALL_TABS: WorkTab[] = ['my', 'created', 'collaborating', 'team', 'reviews', 'workload', 'templates'];

export const WorkPage: React.FC = () => {
  const { t } = useTranslation('work');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { policy, me } = useWorkPolicy();
  const canApprove = useHasPermission('work:approve');
  const canManage = useHasPermission('work:manage');
  const canAssignPerm = useHasPermission('work:assign');
  const canWrite = useHasPermission('work:write');

  // Tab gates: policy `me` is authoritative once loaded; permissions are the
  // pre-load fallback so the page never flashes an empty tab strip.
  const showTeam = me ? (me.can_assign || me.can_review || me.can_manage) : (canAssignPerm || canApprove || canManage);
  const showReviews = canApprove || !!me?.can_review;
  const showTemplates = canManage || !!me?.can_manage;
  const canCreate = me ? me.can_create : canWrite;
  // Workload tab: everyone with work:read once the tenant enables the feature
  // (the route already requires work:read); Team/Departments gate inside.
  const showWorkload = !!policy?.workload_enabled;

  const tabs = useMemo(() => {
    const out: Array<{ key: WorkTab; label: string }> = [
      { key: 'my', label: t('tab_my', { defaultValue: 'My Work' }) as string },
      { key: 'created', label: t('tab_created', { defaultValue: 'Created by Me' }) as string },
      { key: 'collaborating', label: t('tab_collaborating', { defaultValue: 'Collaborating' }) as string },
    ];
    if (showTeam) out.push({ key: 'team', label: t('tab_team', { defaultValue: 'Team Work' }) as string });
    if (showReviews) out.push({ key: 'reviews', label: t('tab_reviews', { defaultValue: 'Reviews' }) as string });
    if (showWorkload) out.push({ key: 'workload', label: t('tab_workload', { defaultValue: 'Workload' }) as string });
    if (showTemplates) out.push({ key: 'templates', label: t('tab_templates', { defaultValue: 'Templates' }) as string });
    return out;
  }, [t, showTeam, showReviews, showWorkload, showTemplates]);

  const requested = params.get('tab') as WorkTab | null;
  const tab: WorkTab = requested && ALL_TABS.includes(requested) && tabs.some((x) => x.key === requested) ? requested : 'my';
  const setTab = (next: WorkTab) => {
    const p = new URLSearchParams(params);
    if (next === 'my') p.delete('tab'); else p.set('tab', next);
    setParams(p, { replace: true });
  };

  const [openCreate, setOpenCreate] = useState(false);
  const [openTemplates, setOpenTemplates] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const bump = useCallback(() => setRefresh((n) => n + 1), []);
  const openItem = (id: string) => navigate(tab === 'reviews' ? `${id}?review=1` : id);

  return (
    <div className={`p-6 space-y-4 ${tab === 'workload' ? 'max-w-5xl' : 'max-w-3xl'}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-h2 text-charcoal flex items-center gap-2">
            <ClipboardList className="w-5 h-5" />
            {t('page_title', { defaultValue: 'Work' })}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('page_sub', { defaultValue: 'Everything you own, created, or contribute to — complete checklists, attach evidence, and submit for review.' })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {showTemplates && (
            <Button variant="outline" onClick={() => setOpenTemplates(true)}>
              <Settings2 className="w-4 h-4 mr-1" />{t('templates', { defaultValue: 'Templates' })}
            </Button>
          )}
          {canCreate && (
            <Button onClick={() => setOpenCreate(true)}>
              <Plus className="w-4 h-4 mr-1" />{t('new', { defaultValue: 'New work item' })}
            </Button>
          )}
        </div>
      </div>

      <div role="tablist" aria-label={t('page_title', { defaultValue: 'Work' }) as string}
        className="flex flex-wrap gap-1 border-b border-border">
        {tabs.map((x) => (
          <button key={x.key} type="button" role="tab" aria-selected={tab === x.key} id={`work-tab-${x.key}`}
            aria-controls={`work-panel-${x.key}`}
            onClick={() => setTab(x.key)}
            className={`px-3 py-2 text-sm -mb-px border-b-2 transition-colors ${tab === x.key
              ? 'border-primary-500 text-foreground font-medium'
              : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {x.label}
          </button>
        ))}
      </div>

      <div role="tabpanel" id={`work-panel-${tab}`} aria-labelledby={`work-tab-${tab}`}>
        {tab === 'my' && (
          <WorkList filter={{ assignee: 'me' }} defaultStatus={OPEN_KEY} refreshKey={refresh} onOpen={openItem}
            emptyLabel={t('my_none', { defaultValue: 'Nothing assigned right now.' }) as string}
            noMatchLabel={t('my_no_match', { defaultValue: 'No work matches your filters.' }) as string} />
        )}
        {tab === 'created' && (
          <WorkList filter={{ created_by: 'me' }} refreshKey={refresh} showOwner onOpen={openItem}
            emptyLabel={t('created_none', { defaultValue: "You haven't created any work items yet." }) as string}
            noMatchLabel={t('list_no_match', { defaultValue: 'No work items match your filters.' }) as string} />
        )}
        {tab === 'collaborating' && (
          <WorkList filter={{ collaborating: 'me' }} defaultStatus={OPEN_KEY} refreshKey={refresh} showOwner onOpen={openItem}
            emptyLabel={t('collab_none', { defaultValue: "You're not collaborating on any work right now." }) as string}
            noMatchLabel={t('list_no_match', { defaultValue: 'No work items match your filters.' }) as string} />
        )}
        {tab === 'team' && (
          <div className="space-y-4">
            <ProjectsStrip onOpen={openItem} refreshKey={refresh} />
            <WorkList filter={{ scope: 'team' }} refreshKey={refresh} showOwner onOpen={openItem}
              emptyLabel={t('team_none', { defaultValue: 'No work items in your team yet.' }) as string}
              noMatchLabel={t('list_no_match', { defaultValue: 'No work items match your filters.' }) as string} />
          </div>
        )}
        {tab === 'reviews' && <ReviewQueue onOpen={openItem} refreshKey={refresh} />}
        {tab === 'workload' && <WorkloadPage refreshKey={refresh} />}
        {tab === 'templates' && (
          <Card><CardContent className="p-6 space-y-3">
            <p className="text-sm text-foreground">
              {t('templates_intro', { defaultValue: 'Work types are reusable packs — default checklist, evidence requirements, priority and optional recurrence — that anyone creating work can pick from.' })}
            </p>
            <Button onClick={() => setOpenTemplates(true)}>
              <Settings2 className="w-4 h-4 mr-1" />{t('templates_manage', { defaultValue: 'Manage templates' })}
            </Button>
          </CardContent></Card>
        )}
      </div>

      <CreateWorkDialog open={openCreate} onClose={() => setOpenCreate(false)} onCreated={bump} />
      <ManageTemplatesDialog open={openTemplates} onClose={() => setOpenTemplates(false)} />
    </div>
  );
};

/** Reviews tab: what awaits the caller's approval (queue=review resolves the
 * APPROVE population server-side; SoD still blocks self-approval on submit). */
const ReviewQueue: React.FC<{ onOpen: (id: string) => void; refreshKey: number }> = ({ onOpen, refreshKey }) => {
  const { t } = useTranslation('work');
  const [queue, setQueue] = useState<WorkItem[]>([]);
  const [subCounts, setSubCounts] = useState<Record<string, SubitemRollup>>({});
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    listWorkItems({ queue: 'review', limit: 100 })
      .then((p) => { if (cancelled) return; setQueue(p.items); setSubCounts(p.subitem_counts || {}); })
      .catch((e) => { if (!cancelled) toast.error(getApiErrorMessage(e, t('queue_load_failed', { defaultValue: 'Could not load the review queue.' }))); })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [refreshKey, t]);
  const { nameOf } = usePeopleNames(queue.map((w) => w.owner_employee_id));
  return (
    <div className="space-y-2">
      <h2 className="text-h4 text-charcoal">
        {t('awaiting', { defaultValue: 'Awaiting your review' })}
        {queue.length > 0 && <span className="text-muted-foreground"> · {queue.length}</span>}
      </h2>
      <Card><CardContent className="p-0">
        {queue.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">
            {loaded ? t('queue_none', { defaultValue: 'Nothing awaiting review — submitted work will appear here.' }) : '…'}
          </p>
        ) : queue.map((w) => {
          const sub = subCounts[w.id];
          return (
            <button key={w.id} type="button" onClick={() => onOpen(w.id)}
              className="flex w-full items-center justify-between gap-3 border-b border-border py-3 px-4 text-left hover:bg-muted/50">
              <div className="min-w-0">
                <p className="text-foreground truncate">
                  {w.title}
                  {!!w.parent_id && <span className="ml-2 text-xs text-muted-foreground">{t('subitem_tag', { defaultValue: 'sub-item' })}</span>}
                </p>
                <p className="text-xs text-muted-foreground tabular-nums">
                  {w.owner_employee_id ? `${nameOf(w.owner_employee_id)} · ` : ''}
                  {w.submitted_at
                    ? `${t('submitted_at', { defaultValue: 'submitted' })} ${fmtDT(w.submitted_at)}`
                    : `${t('updated', { defaultValue: 'Updated' })} ${fmtDT(w.updated_at)}`}
                  {sub?.total ? ` · ${sub.done}/${sub.total} ${t('sub_done', { defaultValue: 'sub-items done' })}` : ''}
                </p>
              </div>
              <WorkStatusBadge status={w.status} />
            </button>
          );
        })}
      </CardContent></Card>
    </div>
  );
};

/** Team Work: project chips → a quick modal of that project's items. */
const ProjectsStrip: React.FC<{ onOpen: (id: string) => void; refreshKey: number }> = ({ onOpen, refreshKey }) => {
  const { t } = useTranslation('work');
  const [projects, setProjects] = useState<WorkProject[]>([]);
  const [projOpen, setProjOpen] = useState<WorkProject | null>(null);
  const [projItems, setProjItems] = useState<WorkItem[]>([]);
  useEffect(() => {
    let cancelled = false;
    listProjects({ limit: 50 }).then((p) => { if (!cancelled) setProjects(p.items); }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [refreshKey]);
  const { nameOf } = usePeopleNames(projItems.map((w) => w.owner_employee_id));
  if (projects.length === 0) return null;

  const openProject = (id: string) => {
    const p = projects.find((x) => x.id === id);
    if (!p) return;
    setProjOpen(p); setProjItems([]);
    listWorkItems({ project_id: p.id, limit: 100 }).then((r) => setProjItems(r.items))
      .catch((e) => toast.error(getApiErrorMessage(e, t('proj_load_failed', { defaultValue: 'Could not load the project.' }))));
  };

  return (
    <>
      {/* ⚠️ A PICKER, NOT A WALL. This rendered every project as a chip in a
          flex-wrap — at Fern that is roughly forty chips filling the screen
          before a single work item is visible, so the list the page exists
          for starts below the fold. A long list of equal-weight options is a
          dropdown; chips are for a handful you want always visible. */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground shrink-0">
          {t('projects_label', { defaultValue: 'Projects' })}
        </span>
        <Select value="" onValueChange={openProject}>
          <SelectTrigger className="w-full max-w-xs">
            <SelectValue placeholder={t('projects_placeholder', {
              defaultValue: `Open a project (${projects.length})`,
              count: projects.length,
            })} />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            {projects.map((p) => (
              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {projOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-foreground/20 p-4" onClick={() => setProjOpen(null)}>
          <div className="w-full max-w-lg rounded-lg bg-background border border-border p-5 space-y-3 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}>
            <div>
              <h3 className="text-h4 text-charcoal">{projOpen.name}</h3>
              {projOpen.description && <p className="text-sm text-muted-foreground">{projOpen.description}</p>}
            </div>
            {projItems.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('proj_empty', { defaultValue: 'No work items in this project yet.' })}</p>
            ) : projItems.map((w) => (
              <button key={w.id} type="button"
                className="flex w-full items-center justify-between gap-2 border-b border-border py-2 text-left hover:bg-muted/50"
                onClick={() => { setProjOpen(null); onOpen(w.id); }}>
                <span className="min-w-0">
                  <span className="text-foreground truncate block">{w.title}</span>
                  {w.owner_employee_id && <span className="text-xs text-muted-foreground">{nameOf(w.owner_employee_id)}</span>}
                </span>
                <WorkStatusBadge status={w.status} />
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default WorkPage;
